/**
 * Measurements as a tailor actually writes them.
 *
 * Nobody at a shop counter writes `36.5`. They write `36 1/2`, or `36½`, or `36-1/2`, because
 * a tape measure is marked in halves, quarters and eighths and that is the language of the
 * trade. A capture form that rejects those is a form the shop works around by writing on paper
 * — which is the failure mode this whole product exists to end.
 *
 * So entry is parsed here, and the parse is shared: the browser and the API agree byte for byte
 * about what "36 1/2" means, and the value is STORED as a single number so that nothing
 * downstream — an order snapshot, a report, a comparison against last year's profile — ever has
 * to parse a string again.
 *
 * Storing a decimal loses nothing. Every fraction a tape measure carries is a binary fraction
 * (1/2, 1/4, 1/8, 1/16), and those are represented EXACTLY by a double. 36.5 is exactly 36½,
 * 36.125 is exactly 36⅛, and formatting back is lossless. Thirds are the one exception, and
 * no tape measure has them.
 */

/** The vulgar fractions a phone keyboard or a paste from WhatsApp can produce. */
const UNICODE_FRACTIONS: Readonly<Record<string, number>> = Object.freeze({
  '½': 0.5,
  '¼': 0.25,
  '¾': 0.75,
  '⅛': 0.125,
  '⅜': 0.375,
  '⅝': 0.625,
  '⅞': 0.875,
  '⅓': 1 / 3,
  '⅔': 2 / 3,
});

/** Denominators a tape measure is actually marked in, finest last. */
const TAPE_DENOMINATORS = [2, 4, 8, 16] as const;

const ASCII_FOR: Readonly<Record<string, string>> = Object.freeze({
  '0.5': '1/2',
  '0.25': '1/4',
  '0.75': '3/4',
  '0.125': '1/8',
  '0.375': '3/8',
  '0.625': '5/8',
  '0.875': '7/8',
});

const UNICODE_FOR: Readonly<Record<string, string>> = Object.freeze({
  '1/2': '½',
  '1/4': '¼',
  '3/4': '¾',
  '1/8': '⅛',
  '3/8': '⅜',
  '5/8': '⅝',
  '7/8': '⅞',
  '1/16': '¹⁄₁₆',
});

/**
 * Parse a measurement as written. Returns `null` for anything that is not a number — the
 * caller decides whether that is an empty field or a rejected one.
 *
 * Accepts: `36`, `36.5`, `36 1/2`, `36-1/2`, `36½`, `36 ½`, `1/2`, `½`, and any of those with
 * a trailing unit or inch mark (`36 1/2"`, `36.5 in`, `92 cm`).
 */
export function parseMeasurementValue(input: string | number | null | undefined): number | null {
  if (typeof input === 'number') return Number.isFinite(input) ? input : null;
  if (input === null || input === undefined) return null;

  let text = input.trim().toLowerCase();
  if (text.length === 0) return null;

  // Drop a trailing unit or inch mark; the field already knows what it is measured in.
  text = text.replace(/(?:"|”|''|\s*(?:in|inch|inches|cm|cms|centimetre?s?))\s*$/u, '').trim();
  if (text.length === 0) return null;

  // A vulgar fraction, with or without a whole part: `36½`, `36 ½`, `½`.
  const unicode = /^(\d+)?\s*([½¼¾⅛⅜⅝⅞⅓⅔])$/u.exec(text);
  if (unicode) {
    const whole = unicode[1] ? Number(unicode[1]) : 0;
    const fraction = UNICODE_FRACTIONS[unicode[2] as string];
    return fraction === undefined ? null : whole + fraction;
  }

  // A mixed number: `36 1/2`, `36-1/2`. The separator may be a space or a hyphen, which is how
  // it gets written when somebody is avoiding the space bar.
  const mixed = /^(\d+)[\s-]+(\d+)\s*\/\s*(\d+)$/u.exec(text);
  if (mixed) {
    const denominator = Number(mixed[3]);
    if (denominator === 0) return null;
    return Number(mixed[1]) + Number(mixed[2]) / denominator;
  }

  // A bare fraction: `1/2`, `3/8`.
  const fraction = /^(\d+)\s*\/\s*(\d+)$/u.exec(text);
  if (fraction) {
    const denominator = Number(fraction[2]);
    if (denominator === 0) return null;
    return Number(fraction[1]) / denominator;
  }

  // A plain decimal. `36,5` is included because a numeric keypad in some locales produces it.
  const decimal = /^(\d+)(?:[.,](\d+))?$/u.exec(text);
  if (decimal) {
    const value = Number(`${decimal[1]}.${decimal[2] ?? '0'}`);
    return Number.isFinite(value) ? value : null;
  }

  return null;
}

export interface FormatMeasurementOptions {
  /** Render `½` rather than `1/2`. On by default — it is how a job card is written. */
  unicode?: boolean;
  /** Appended after the number, e.g. `in`. */
  unit?: string;
}

/**
 * Render a stored value the way the shop wrote it: `36.5` back to `36½`.
 *
 * A value that lands exactly on a tape-measure marking is shown as a fraction; anything else
 * (an imported decimal, a centimetre reading) is shown as a decimal, because inventing a
 * fraction for 36.4 would be a lie about the precision of the measurement.
 */
export function formatMeasurementValue(
  value: number,
  options: FormatMeasurementOptions = {},
): string {
  const { unicode = true, unit } = options;
  if (!Number.isFinite(value)) return '';

  const suffix = unit ? ` ${unit}` : '';
  const sign = value < 0 ? '-' : '';
  const absolute = Math.abs(value);
  const whole = Math.floor(absolute);
  const remainder = absolute - whole;

  if (remainder === 0) return `${sign}${whole}${suffix}`;

  for (const denominator of TAPE_DENOMINATORS) {
    const numerator = remainder * denominator;
    if (Number.isInteger(numerator)) {
      const ascii = ASCII_FOR[String(remainder)] ?? `${numerator}/${denominator}`;
      const rendered = unicode ? (UNICODE_FOR[ascii] ?? ascii) : ascii;
      // No space before a vulgar fraction — `36½` is how it is written by hand.
      const separator = unicode && UNICODE_FOR[ascii] ? '' : ' ';
      return whole === 0
        ? `${sign}${rendered}${suffix}`
        : `${sign}${whole}${separator}${rendered}${suffix}`;
    }
  }

  // Not a tape-measure marking: say the number, rounded to something a person can read.
  return `${sign}${Number(absolute.toFixed(2))}${suffix}`;
}

/** True when the text is something `parseMeasurementValue` can read. */
export function isParsableMeasurement(input: string): boolean {
  return parseMeasurementValue(input) !== null;
}
